import type i18n from 'i18next';
import { initReactI18next, useTranslation, I18nextProvider, type I18nextProviderProps } from 'react-i18next';

interface ILangConfig {
    i18nInstance: typeof i18n;
    currentLang: string;
    fallbackLang?: string;
}

type TInitFn = (config: ILangConfig) => void;

class ReoI18n {
    private _loadedPackages: string[] = [];

    private _i18n!: typeof i18n;

    private _initTask: Promise<void> | null = null;

    public init: TInitFn = async (config) => {
        await this._initAndStorePromise(config);
    };

    public get initTask(): Promise<void> | null {
        return this._initTask;
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    public get Provider(): React.FunctionComponent<I18nextProviderProps> {
        return I18nextProvider;
    }

    private async _initAndStorePromise(config: ILangConfig): Promise<void> {
        const promise = this._init(config);
        this._initTask = promise;
        try {
            await promise;
        }
        finally {
            this._initTask = null;
        }
    }

    private async _init(config: ILangConfig): Promise<void> {
        const { i18nInstance, currentLang, fallbackLang = 'en' } = config;
        this._i18n = i18nInstance;

        const i = this._i18n.use(initReactI18next); // passes i18n down to react-i18next
        await i.init({
            // debug: true,
            resources: {},
            lng: currentLang,
            fallbackLng: fallbackLang,
            interpolation: {
                ...i.options.interpolation,
                prefix: '{',
                suffix: '}',
            },
        });
    }

    public loadResource(
        packageNames: string[],
        i18nPackageText: Record<string, Record<string, string>>,
        lang?: string,
    ): void {
        const currentLang = lang ?? this._i18n.language;

        const toLoadPackage = packageNames.filter((name) => {
            return !this._i18n?.hasResourceBundle(currentLang, name);
        });

        if (toLoadPackage.length > 0) {
            //把语言包注入i18n
            for (const pName in i18nPackageText) {
                this._i18n.addResourceBundle(currentLang, pName, i18nPackageText[pName]);
            }
            this._loadedPackages = Array.from(new Set([...this._loadedPackages, ...toLoadPackage]));
        }
    }
}

export { useTranslation };

export default ReoI18n;
